import { useEffect, useRef, useState } from 'react';

export type CalendarDay = { date: string; day: number };

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function parseCalendarDate(value: string | null | undefined, fallback = new Date()): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) return new Date(fallback.getFullYear(), fallback.getMonth(), 1);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return new Date(fallback.getFullYear(), fallback.getMonth(), 1);
  }
  return parsed;
}

export function calendarDateValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function shiftCalendarMonth(month: Date, amount: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + amount, 1);
}

export function calendarDays(month: Date): Array<CalendarDay | null> {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  return [
    ...Array.from({ length: offset }, () => null),
    ...Array.from({ length: count }, (_, index) => {
      const date = new Date(month.getFullYear(), month.getMonth(), index + 1);
      return { date: calendarDateValue(date), day: index + 1 };
    }),
  ];
}

export function isCalendarDateSelectable(date: string, min?: string, max?: string): boolean {
  return (!min || date >= min) && (!max || date <= max);
}

export function SellerReportDatePicker({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(parseCalendarDate(value));
  const root = useRef<HTMLDivElement | null>(null);
  const monthLabel = new Intl.DateTimeFormat('en-ZA', { month: 'long', year: 'numeric' }).format(visibleMonth);
  const selectedLabel = value
    ? new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }).format(parseCalendarDate(value))
    : 'Choose date';

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const openCalendar = () => {
    setVisibleMonth(parseCalendarDate(value));
    setOpen(true);
  };

  return <div ref={root} className="relative min-w-0">
    <span className="block text-sm font-medium text-slate-700">{label}</span>
    <button type="button" aria-label={`${label}: ${selectedLabel}`} aria-haspopup="dialog" aria-expanded={open}
      onClick={open ? () => setOpen(false) : openCalendar}
      className="mt-1 flex min-h-11 w-full items-center justify-between gap-3 rounded border border-slate-300 bg-white px-3 text-left text-sm text-slate-900 hover:border-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-red-700">
      <span>{selectedLabel}</span>
      <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4 shrink-0 text-slate-500">
        <rect x="3" y="4.5" width="14" height="13" rx="1.5" /><path d="M6.5 2.8v3.4M13.5 2.8v3.4M3 8h14" />
      </svg>
    </button>
    {open && <div role="dialog" aria-label={`${label} calendar`} className="absolute left-0 top-full z-50 mt-2 w-[min(20rem,calc(100vw-3rem))] rounded-md border border-slate-200 bg-white p-3 shadow-xl">
      <div className="mb-2 flex items-center justify-between gap-2">
        <button type="button" aria-label="Previous month" onClick={() => setVisibleMonth((month) => shiftCalendarMonth(month, -1))}
          className="flex h-9 items-center justify-center rounded border border-slate-200 px-2 text-xs font-medium text-slate-700 hover:bg-slate-50">Prev</button>
        <p aria-live="polite" className="text-sm font-semibold text-slate-900">{monthLabel}</p>
        <button type="button" aria-label="Next month" onClick={() => setVisibleMonth((month) => shiftCalendarMonth(month, 1))}
          className="flex h-9 items-center justify-center rounded border border-slate-200 px-2 text-xs font-medium text-slate-700 hover:bg-slate-50">Next</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase text-slate-500">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => <span key={day} className="py-1">{day}</span>)}
      </div>
      <div role="grid" aria-label={monthLabel} className="grid grid-cols-7 gap-1">
        {calendarDays(visibleMonth).map((item, index) => item
          ? <button key={item.date} type="button" role="gridcell" aria-label={new Intl.DateTimeFormat('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(parseCalendarDate(item.date))} aria-pressed={item.date === value} disabled={!isCalendarDateSelectable(item.date, min, max)}
              onClick={() => { onChange(item.date); setOpen(false); }}
              className={`h-9 rounded text-sm tabular-nums transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-700 ${item.date === value ? 'bg-red-800 font-semibold text-white' : 'text-slate-800 hover:bg-red-50'} disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent`}>{item.day}</button>
          : <span key={`blank-${index}`} aria-hidden="true" className="h-9" />)}
      </div>
    </div>}
  </div>;
}
